import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Layout,
  Menu,
  Drawer,
  Button,
  Avatar,
  Dropdown,
  Modal,
} from "antd";

import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MenuOutlined from "@ant-design/icons/es/icons/MenuOutlined";
import LogoutOutlined from "@ant-design/icons/es/icons/LogoutOutlined";
import GlobalOutlined from "@ant-design/icons/es/icons/GlobalOutlined";
import {
  MenuUnfoldOutlined,
  NotificationOutlined,
} from "@ant-design/icons";
import Localize, { getCurrentLocale, l } from "../../libs/localization";
import Logo from "../../assets/images/blinko-azul.png";
import LogoHR from "../../assets/images/blinko-black.png";

import "./site.layout.scss";
import { get } from "../../libs/helpers/ioc";
import SearchBar from "../../pages/search/Searchbar/SearchBar";
import { goTo, setLanguageAction } from "../../actions";
import LocalizedFlag from "../../components/Localized/LocalizedFlag";
import Config from "../../Config";
import SmallTenantSelector from "../../components/SmallTenantSelector/SmallTenantSelector";
import { refreshPage } from "../../libs/utils";
import { logoutApi } from "../../api/auth.api";
import usePermissions from "../../hooks/usePermissions";
import { getFromLocalStorage } from "../../libs/helpers/localstorage";
import router from "../../libs/router";
import history from "../../store/history";
import routes from "../../routes";
import ProfileIcon from "../../design-library/icons/ProfileIcon.component";
import AgentChat from "../../components/AgentChat/AgentChat.component";
import {
  getDefaultProfilePage,
  shouldShowProfilePage,
} from "../../libs/profile";

const { Header, Content, Footer, Sider } = Layout;

const SIDER_WIDTH = 220;

const BOX_WIDTH = "1400px";

function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Call handler right away so state gets updated with initial window size
    handleResize();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
}

const SiteLayout = (props) => {
  const { children, id } = props;

  const [isMobile, setIsMobile] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isHR, setIsHR] = useState(false);

  const user = useSelector((state) => state.auth.user);
  const tenant = getFromLocalStorage("lefacWebTenant");
  const permissions = usePermissions();

  const dispatch = useDispatch();

  const userInitials = [user?.name, user?.surname]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join("");

  const showDrawer = () => {
    setVisible(true);
  };
  const onClose = () => {
    setVisible(false);
  };

  const size = useWindowSize();

  const handleMenuChange = (_size) => {
    if (_size.width <= 575) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  };

  useEffect(() => {
    handleMenuChange(size);
  }, [size]);

  useEffect(() => {
    const userProfiles = user?.licence?.tenants?.[tenant?.data]?.profiles;
    if (userProfiles) {
      Object.keys(userProfiles).map((profile) => {
        if (
          userProfiles[profile].name === "external_contact" ||
          userProfiles[profile].name === "hr_database"
        )
          setIsHR(true);
      });
    }
  }, [user]);

  const menu = routes.filter((r) => r.showInMenu);
  const permissions_ = get("scopes");

  const filteredMenu = menu.filter((m) =>
    permissions_ && m.scopes && m.scopes.length > 0
      ? (m.scopes || []).some((s) =>
          Object.keys(permissions_ || {}).some((p) => p === s),
        )
      : m,
  );

  // Order and grouping of the main menu. A string is a top-level page (by
  // route id); an object is a dropdown group. Pages the user may not see
  // (scopes) drop out, a group left with one page shows that page directly,
  // and any page not listed here is appended so new menu routes never vanish.
  //
  // `page` makes the group's own title a link to that page (hover opens the
  // dropdown, click goes to the page). Touch screens cannot hover, so there
  // (and in the mobile drawer) the page is listed inside the dropdown instead.
  const menuLayout = [
    "home",
    "tag",
    "companySearch",
    "contactSearch",
    {
      id: "group-pitch",
      page: "pitchSearch",
      items: ["pitchScores"],
    },
    {
      id: "group-campaigns",
      title: Localize("MENU.GROUP.CAMPAIGNS"),
      icon: <NotificationOutlined />,
      items: ["creaSearch", "brandAdvertisers"],
    },
    "workRelation",
    "dashboard",
  ];

  const findPage = (id) => filteredMenu.find((m) => m.id === id);

  const buildMenu = (linkedParent) => {
    const placed = new Set();
    const tree = menuLayout
      .map((entry) => {
        if (typeof entry === "string") {
          const page = findPage(entry);
          if (page) placed.add(page.id);
          return page;
        }
        const parent = entry.page ? findPage(entry.page) : null;
        const children = entry.items.map(findPage).filter(Boolean);
        if (parent) placed.add(parent.id);
        children.forEach((c) => placed.add(c.id));

        if (parent && linkedParent) {
          // clickable title + dropdown with only the secondary pages
          if (children.length === 0) return parent;
          return {
            ...parent,
            id: entry.id,
            activeId: parent.id,
            titlePath: parent.menuPath || parent.path,
            children,
          };
        }
        // plain dropdown; a linked parent becomes its first entry
        const all = parent ? [parent, ...children] : children;
        if (all.length === 0) return null;
        if (all.length === 1) return all[0];
        return {
          id: entry.id,
          title: entry.title || parent?.title,
          icon: entry.icon || parent?.icon,
          children: all,
        };
      })
      .filter(Boolean);
    return [...tree, ...filteredMenu.filter((m) => !placed.has(m.id))];
  };

  const drawerMenu = buildMenu(false);

  const drawMenuItem = (i) =>
    i.children ? (
      <Menu.SubMenu
        key={i.id}
        icon={i.icon}
        // the linked parent page is not a selectable key, so mark it by hand
        className={
          i.titlePath && i.activeId === id ? "ant-menu-submenu-selected" : ""
        }
        title={
          i.titlePath ? <Link to={i.titlePath}>{i.title}</Link> : i.title
        }
        onTitleClick={
          i.titlePath
            ? ({ domEvent }) => {
                // clicks on the link navigate themselves; this covers the
                // padding around it
                if (!domEvent.target.closest("a")) {
                  dispatch(goTo(i.titlePath));
                }
              }
            : undefined
        }
      >
        {i.children.map((j) => drawMenuItem(j))}
      </Menu.SubMenu>
    ) : (
      i.showInMenu && (
        <Menu.Item key={i.id} icon={i.icon}>
          <Link to={i.menuPath || i.path}>{i.title}</Link>
        </Menu.Item>
      )
    );

  return (
    <Layout className="siteLayout" hasSider={!isMobile}>
      {!isMobile && (
        <Sider
          className="siteLayout__sider"
          width={SIDER_WIDTH}
          theme="light"
        >
          <img
            className="siteLayout__sider-logo"
            alt="logo"
            src={!isHR ? Logo : LogoHR}
            onClick={() => dispatch(goTo("/"))}
          />
          <Menu
            mode="inline"
            className="siteLayout__sider-menu"
            defaultSelectedKeys={[id]}
            selectedKeys={[id]}
          >
            {drawerMenu.map((i) => drawMenuItem(i))}
          </Menu>
        </Sider>
      )}
      <Layout className="siteLayout__main">
      {isMobile && (
        <Button
          type="primary"
          className="siteLayout__mobileMenuButton"
          onClick={() => showDrawer()}
        >
          <MenuOutlined />
        </Button>
      )}
      <Header className="siteLayout__header">
        <Row type="flex" justify="center">
          <Col
            span={24}
            style={{ maxWidth: id === "dashboard" ? "100%" : BOX_WIDTH }}
          >
            <Row
              type="flex"
              justify="space-between"
              align="middle"
              className="siteLayout__header-content"
            >
              <Col span={12}>
                <Row type="flex" justify="start" align="middle">
                  {isMobile && (
                    <img
                      className="siteLayout__header-logo"
                      alt="logo"
                      src={!isHR ? Logo : LogoHR}
                      onClick={() => dispatch(goTo("/"))}
                    />
                  )}
                  <SearchBar />
                </Row>
              </Col>
              <Col span={12}>
                <Row type="flex" justify="end" align="middle">
                  {!isMobile && (
                    <>
                  <Dropdown
                    className="dropdown-language"
                    overlay={
                      <Menu>
                        {(Config.locales || []).map((l, i) => (
                          <Menu.Item key={i}>
                            <Button
                              type="link"
                              onClick={() => {
                                dispatch(setLanguageAction({ language: l }));
                                window.location.reload();
                              }}
                            >
                              {LocalizedFlag({
                                locale: l,
                                style: { height: 10 },
                              })}
                            </Button>
                          </Menu.Item>
                        ))}
                      </Menu>
                    }
                    trigger={["click"]}
                    placement="bottomCenter"
                    arrow
                    style={{ top: "100px" }}
                  >
                    <a
                      className="ant-dropdown-link"
                      onClick={(e) => e.preventDefault()}
                      style={{ marginRight: 30 }}
                    >
                      {LocalizedFlag({
                        locale: getCurrentLocale(),
                      })}
                    </a>
                  </Dropdown>
                  <Dropdown
                    className="dropdown-language"
                    overlay={
                      <Menu>
                        <Menu.Item
                          key="0"
                          style={{
                            textAlign: "center",
                            borderBottom: "1px solid #CCC",
                          }}
                        >
                          <b className="no-pointer">
                            {user && `${user.name} ${user.surname}`}
                          </b>
                        </Menu.Item>
                        {shouldShowProfilePage({ permissions }) && (
                          <Menu.Item
                            key="1"
                            onClick={() => {
                              history.push(
                                getDefaultProfilePage({ permissions }),
                              );
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <ProfileIcon sx={{ fontSize: 14 }} />{" "}
                              {l("PROFILE.PROFILE")}
                            </div>
                          </Menu.Item>
                        )}
                        <Menu.Item
                          key="2"
                          onClick={() => {
                            setShowModal(!showModal);
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <GlobalOutlined /> {Localize("LOGIN.CHANGE_TENANT")}
                          </div>
                        </Menu.Item>
                        <Menu.Item
                          key="3"
                          onClick={async () => {
                            await logoutApi();
                            dispatch(goTo("/logout/do"));
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <LogoutOutlined /> {Localize("LOGIN.LOGOUT")}
                          </div>
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={["click"]}
                    placement="bottomCenter"
                    arrow
                    style={{ top: "100px" }}
                  >
                    <Avatar
                      size="large"
                      className="siteLayout__avatar pointer"
                      icon={
                        userInitials ? undefined : (
                          <MenuUnfoldOutlined className="pointer" />
                        )
                      }
                    >
                      {userInitials}
                    </Avatar>
                  </Dropdown>
                    </>
                  )}
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Header>

      <Drawer
        className="siteLayout__drawer"
        title="Menu"
        placement="right"
        closable={true}
        onClose={onClose}
        visible={visible}
        width={size.width}
      >
        <Link to="/login" onClick={onClose}>
          {Localize("COMMON.LOGIN")}
        </Link>
        <Menu mode="inline" className="siteLayout__drawer-menu">
          {drawerMenu.map((i) => drawMenuItem(i))}
        </Menu>
        <div className="siteLayout__drawer-account">
          <div className="siteLayout__drawer-user">
            {user && `${user.name} ${user.surname}`}
          </div>
          <Menu mode="vertical" className="siteLayout__drawer-menu">
            {shouldShowProfilePage({ permissions }) && (
              <Menu.Item
                key="profile"
                icon={<ProfileIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  onClose();
                  history.push(getDefaultProfilePage({ permissions }));
                }}
              >
                {l("PROFILE.PROFILE")}
              </Menu.Item>
            )}
            <Menu.Item
              key="tenant"
              icon={<GlobalOutlined />}
              onClick={() => {
                onClose();
                setShowModal(!showModal);
              }}
            >
              {Localize("LOGIN.CHANGE_TENANT")}
            </Menu.Item>
            <Menu.Item
              key="logout"
              icon={<LogoutOutlined />}
              onClick={async () => {
                await logoutApi();
                dispatch(goTo("/logout/do"));
              }}
            >
              {Localize("LOGIN.LOGOUT")}
            </Menu.Item>
          </Menu>
          <div className="siteLayout__drawer-languages">
            {(Config.locales || []).map((loc) => (
              <Button
                key={loc}
                type="link"
                onClick={() => {
                  dispatch(setLanguageAction({ language: loc }));
                  window.location.reload();
                }}
              >
                {LocalizedFlag({
                  locale: loc,
                  style: { height: 14, opacity: loc === getCurrentLocale() ? 1 : 0.5 },
                })}
              </Button>
            ))}
          </div>
        </div>
      </Drawer>

      <Content className="">
        <Row type="flex" justify="center">
          <Col
            span={24}
            style={{ maxWidth: id === "dashboard" ? "100%" : BOX_WIDTH }}
            className="siteLayout__content"
          >
            {children}
          </Col>
        </Row>
      </Content>
      <Footer
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
        className="siteLayout__footer"
      >
        <Row
          type="flex"
          justify="space-between"
          style={{
            maxWidth: id === "dashboard" ? "100%" : BOX_WIDTH,
            width: "100%",
          }}
        >
          &copy; BLINKO {new Date().getFullYear().toString()}
          <a style={{}} href="https://synesthesia.it" target="_blank">
            <img
              src="/madewithlove-syn_white.svg"
              alt="Made with heart in Synesthesia"
              height="15"
            />
          </a>
        </Row>
      </Footer>
      <Modal
        title={
          <>
            <GlobalOutlined /> {Localize("LOGIN.CHANGE_TENANT")}
          </>
        }
        visible={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
      >
        <SmallTenantSelector
          onSelect={() => {
            setShowModal(false);
            refreshPage();
          }}
        />
      </Modal>
      <AgentChat />
      </Layout>
    </Layout>
  );
};

export default SiteLayout;
